/**
 * Shared Auth Headers Utility
 *
 * Provides auth headers (including impersonation) for ALL API calls,
 * whether using axios or fetch().
 *
 * Usage:
 * const headers = await getAuthHeaders();
 * fetch(url, { headers });
 */

import { auth } from '../firebase/config';
import { impersonationState } from '../services/impersonationState';

/**
 * Get authentication headers for API requests
 * Includes Bearer token + impersonation headers if applicable
 *
 * @param {string} url - Optional URL to check if it's a support route
 * @returns {Promise<Object>} Headers object
 */
export const getAuthHeaders = async (url = '') => {
  const headers = {
    'Content-Type': 'application/json'
  };

  // Add Bearer token
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  // ✅ CRITICAL: Skip impersonation headers for privileged routes
  // Support/admin operations should always execute as themselves
  const isPrivilegedRoute =
    url.includes('/support/') || url.endsWith('/support') ||
    url.includes('/admin/') || url.endsWith('/admin');
  if (isPrivilegedRoute) {
    return headers;
  }

  // Add impersonation headers if admin is impersonating
  const impersonation = impersonationState.get();
  if (impersonation && impersonation.isImpersonating) {
    headers['X-Impersonate-User'] = impersonation.targetUserId;
    headers['X-Impersonation-Session'] = impersonation.sessionId;
  }

  return headers;
};

/**
 * Get auth token (for backward compatibility with existing code)
 */
export const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};
